// Bulk Pay Allocation page.
//
// Lists the timecards a user can approve (the same set as the dashboard "To Do/Approve"
// tab) in an ag-grid, with readonly identity columns on the left for sorting/filtering.
// Each row is a master row that expands to the full <allocation> editor (reused verbatim
// from the single-timecard page via <AllocationDetail>), so an approver can split a
// timecard's pay across projects without navigating to each timecard.
//
// Data in : GET /restv2/getTimecards/toApprove  (rows already carry timecard_object /
//           broken_object / overrides), GET /restv2/projects, GET /restv2/entities.
// Data out: PATCH /restv2/timecard/ { isBatchUpdate:true, id, overrides } — the light
//           "save overrides only, no recalc" path (allocation doesn't change `broken`).

import { createApp } from 'vue'
window.Vue = createApp;
window.$ = window.jQuery = jQuery;

import { AgGridVue } from 'ag-grid-vue3'
import 'ag-grid-enterprise'
import { LicenseManager } from 'ag-grid-enterprise'
LicenseManager.setLicenseKey('CompanyName=Revolution Payroll,LicensedGroup=ProBooks,LicenseType=MultipleApplications,LicensedConcurrentDeveloperCount=1,LicensedProductionInstancesCount=0,AssetReference=AG-011578,ExpiryDate=4_December_2021_[v2]_MTYzODU3NjAwMDAwMA==5da9d3c2699f550dc7daf171582ca3ed')
import '../node_modules/ag-grid-community/dist/styles/ag-grid.css';
import '../node_modules/ag-grid-community/dist/styles/ag-theme-balham.css';

import { createApp as createDetailApp } from 'vue'
import Axios from './classes/Axios.js';
import AllocationDetail from './components/AllocationDetail.vue';
require('./classes/PrototypeExt.js');

// Standard (core-resolved) ag-grid detail renderer that mounts the AllocationDetail Vue
// component itself. We mount manually rather than registering AllocationDetail as an
// ag-grid-vue3 FRAMEWORK component because that path resolves components by a fragile
// parent-chain name search that fails here (and its failure also disrupts the detail
// row's creation on expand). A plain init()/getGui() class registered under
// gridOptions.components is resolved by ag-grid core directly. ag-grid passes the master
// row + detailCellRendererParams as `params`.
class AllocationDetailRenderer {
    init(params) {
        this.eGui = document.createElement('div');
        this.app = createDetailApp(AllocationDetail, { params: params });
        this.app.mount(this.eGui);
    }
    getGui() { return this.eGui; }
    refresh() { return false; }
    destroy() { if (this.app) { try { this.app.unmount(); } catch (e) { /* noop */ } } }
}

// Decode a field that may arrive as a JSON string or an already-decoded object.
function decode(v) {
    if (v == null) return null;
    if (typeof v === 'object') return v;
    if (typeof v === 'string') { try { return JSON.parse(v); } catch (e) { return null; } }
    return null;
}

function freshState() {
    return { touched: false, dirty: false, valid: true, saving: false, savedOk: false, error: '', payload: null };
}

// Sentinel id for the "(No project)" allocation line (mirrors allocation.vue).
const NO_PROJECT = '__none__';

const app = createApp({

    components: {
        'ag-grid-vue': AgGridVue,
    },

    data() {
        return {
            oasisApi: new Axios('/restv2'),
            loading: false,
            error: '',

            rowData: [],          // the timecards-to-approve
            projects: [],         // entity PROJECT list (allocation dropdowns)
            showProjects: true,   // entity flag — allocation only meaningful when projects are used
            showAssignedProjects: false,

            // Per-row allocation UI state keyed by timecard id. Reactive, and read by the
            // detail renderer (which runs as its own app and cannot keep its own data()).
            uiState: {},

            savingAll: false,
            savedCount: 0,

            gridApi: null,
            columnApi: null,
            columnDefs: [],
            gridOptions: null,
        };
    },

    created() {
        const self = this;

        this.columnDefs = [
            // Master-row expand toggle.
            { headerName: '', colId: '_expand', maxWidth: 46, minWidth: 46, width: 46,
              pinned: 'left', sortable: false, filter: false, resizable: false,
              suppressSizeToFit: true, cellRenderer: 'agGroupCellRenderer',
              valueGetter: () => '' },

            { headerName: 'Status', field: 'status_full', minWidth: 140, width: 150, pinned: 'left',
              tooltipField: 'status_full' },

            // Week Ending links to the timecard itself (opens in a new tab), matching
            // Timecard History. valueGetter stays so the date floating-filter/sort work.
            { headerName: 'Week Ending', field: 'week_ending', minWidth: 95, width: 105, pinned: 'left',
              valueGetter: (p) => (p.data && p.data.week_ending ? String(p.data.week_ending).substring(0, 10) : ''),
              cellRenderer: (p) => (p.data && p.data.hash && p.value)
                  ? `<a href="/oasis/timecard/create/${p.data.hash}" target="_blank" rel="noopener">${p.value}</a>`
                  : (p.value || '') },

            { headerName: 'Last Name', colId: 'last_name', minWidth: 130,
              valueGetter: (p) => self.employeePart(p.data, 0) },

            { headerName: 'First Name', colId: 'first_name', minWidth: 130,
              valueGetter: (p) => self.employeePart(p.data, 1) },

            { headerName: 'Title', colId: 'job_title_override', minWidth: 150,
              valueGetter: (p) => {
                  const a = p.data ? decode(p.data.assignment_object) : null;
                  return (a && a.job_title_override) ? a.job_title_override : '';
              } },

            { headerName: 'Local', field: 'union_local', minWidth: 110,
              valueFormatter: (p) => p.value || 'Non Union' },

            { headerName: 'Dept', field: 'department', minWidth: 130,
              valueFormatter: (p) => p.value || '' },

            // Gross Pay — the timecard total (same source/format as Timecard History).
            { headerName: 'Gross Pay', colId: 'gross', minWidth: 110, width: 120,
              cellClass: 't-right bold', filter: 'agNumberColumnFilter',
              valueGetter: (p) => parseFloat((p.data && p.data.total_pay) || 0),
              valueFormatter: (p) => p.value.formatNumber() },

            // Total Allocated — % of the timecard committed to real projects (excludes the
            // "(No project)" line and the unallocated remainder). Reads the current split if
            // the row's been edited, else the saved overrides.allocation.
            { headerName: 'Total Allocated', colId: '_alloc_pct', minWidth: 130, width: 140,
              cellClass: 't-right', filter: 'agNumberColumnFilter',
              valueGetter: (p) => self.projectAllocatedPct(p.data),
              valueFormatter: (p) => (p.value != null ? p.value + '%' : '') },

            // Right-hand allocation status; add/delete rows happen in the expanded detail.
            { headerName: 'Allocation', colId: '_allocation', minWidth: 125, width: 145,
              pinned: 'right', sortable: false, filter: false,
              valueGetter: (p) => self.allocationStatusText(p.data),
              cellClass: (p) => self.allocationStatusClass(p.data) },
        ];

        this.gridOptions = {
            masterDetail: true,
            // Standard component resolved by ag-grid core (NOT the Vue framework factory).
            components: { allocationDetail: AllocationDetailRenderer },
            detailCellRenderer: 'allocationDetail',
            detailRowAutoHeight: true,
            detailCellRendererParams: () => ({ ctx: self }),
            // Expanding a master doesn't always auto re-flatten the client-side model here,
            // which leaves the detail row uncreated — force the map refresh on expand, and
            // redraw just the toggled master row so its active-row highlight updates (redraw
            // is scoped to the master node, so the expanded detail's Vue app isn't recreated).
            onRowGroupOpened: (e) => {
                if (!self.gridApi) return;
                self.gridApi.refreshClientSideRowModel('map');
                if (e && e.node) self.gridApi.redrawRows({ rowNodes: [e.node] });
            },
            // Light-blue highlight on the timecard currently being allocated.
            rowClassRules: {
                'allocation-active-row': (p) => !!(p.node && p.node.master && p.node.expanded),
            },

            rowSelection: 'multiple',
            animateRows: true,
            suppressRowClickSelection: true,
            defaultColDef: {
                resizable: true,
                sortable: true,
                filter: true,
                floatingFilter: true,
                menuTabs: [],
                filterParams: { buttons: ['reset', 'apply'], debounceMs: 200 },
            },
            overlayLoadingTemplate: '<span class="ag-overlay-loading-center">Loading timecards…</span>',
            overlayNoRowsTemplate: '<span class="ag-overlay-no-rows-center">No timecards awaiting your approval.</span>',
            onGridReady: (e) => { self.gridApi = e.api; self.columnApi = e.columnApi; },
        };
    },

    methods: {
        // Per-row UI state accessor (seeded at load; never mutated during render).
        rowState(id) {
            return this.uiState[id] || freshState();
        },

        // "Last, First" -> [0]=Last, [1]=First.
        employeePart(data, idx) {
            if (!data || !data.employee) return '';
            const parts = String(data.employee).split(',');
            return (parts[idx] || '').trim();
        },

        // Sum of percent allocated to real projects (not "(No project)", not unallocated).
        projectAllocatedPct(data) {
            if (!data) return 0;
            const s = this.uiState[data.id];
            const alloc = (s && s.payload) ? s.payload : ((decode(data.overrides) || {}).allocation);
            if (!Array.isArray(alloc)) return 0;
            const pct = alloc.reduce((t, r) => {
                if (!r || r.id == null || String(r.id) === NO_PROJECT) return t;
                return t + (Number(r.percent) || 0);
            }, 0);
            return Math.round(pct * 100) / 100;
        },

        // True when the row carries a persisted allocation split.
        hasSavedAllocation(data) {
            const a = (decode(data.overrides) || {}).allocation;
            return Array.isArray(a) && a.length > 0;
        },

        allocationStatusText(data) {
            if (!data) return '';
            const s = this.uiState[data.id];
            // Being edited this session (pending) → live status.
            if (s && s.touched && (s.dirty || s.saving)) {
                if (!s.valid) return '⚠ Doesn’t reconcile';
                return '● Unsaved split';
            }
            // Fully allocated to projects → nothing to flag, leave it blank.
            if (this.projectAllocatedPct(data) >= 100) return '';
            // Saved (this session or previously) but not fully allocated.
            if ((s && s.savedOk) || this.hasSavedAllocation(data)) return '✓ Saved';
            return 'Not allocated';
        },
        allocationStatusClass(data) {
            if (!data) return '';
            const s = this.uiState[data.id];
            if (s && s.touched && (s.dirty || s.saving)) return s.valid ? 'alloc-dirty' : 'alloc-bad';
            if (this.projectAllocatedPct(data) >= 100) return '';
            if ((s && s.savedOk) || this.hasSavedAllocation(data)) return 'alloc-ok';
            return 'alloc-muted';
        },

        // ---- load ----
        async loadAll() {
            this.loading = true;
            this.error = '';
            try {
                await Promise.all([this.getEntity(), this.getProjects()]);
                await this.getTimecards();
            } catch (e) {
                this.error = (e && e.message) ? e.message : 'Failed to load timecards.';
            } finally {
                this.loading = false;
            }
        },

        async getEntity() {
            try {
                const res = await this.oasisApi.get('/entities');
                const d = res && res.data ? res.data : {};
                if (d.showProjects !== undefined) this.showProjects = !!d.showProjects;
                if (d.showAssignedProjects !== undefined) this.showAssignedProjects = !!d.showAssignedProjects;
            } catch (e) { /* non-fatal: keep showProjects default */ }
        },

        async getProjects() {
            const res = await this.oasisApi.get('/projects');
            this.projects = (res && Array.isArray(res.data)) ? res.data : (res && res.data && res.data.results) || [];
        },

        async getTimecards() {
            const res = await this.oasisApi.get('/getTimecards/toApprove');
            const body = res && res.data ? res.data : {};
            const rows = Array.isArray(body) ? body : (body.results || []);
            // Seed per-row UI state up front so the detail/status cell never mutate it during render.
            const state = {};
            rows.forEach((r) => { if (r && r.id != null) state[r.id] = freshState(); });
            this.uiState = state;
            this.rowData = rows;
        },

        // Compare an emitted split against what's already persisted so a freshly-expanded,
        // already-saved timecard doesn't get flagged "unsaved" by its mount-time emit.
        matchesSaved(row, payload) {
            const saved = (decode(row.overrides) || {}).allocation;
            if (!Array.isArray(saved) || !Array.isArray(payload)) return false;
            const norm = (a) => JSON.stringify((a || []).map((r) => ({
                id: String(r.id), amount: Number(r.amount) || 0,
                allowances: Number(r.allowances) || 0, percent: Number(r.percent) || 0,
            })));
            return norm(saved) === norm(payload);
        },

        // ---- called by each detail on change / save ----
        onRowAllocationChanged(row, payload, isValid) {
            const s = this.uiState[row.id] || (this.uiState[row.id] = freshState());
            const clean = this.matchesSaved(row, payload);
            s.touched = true;
            s.valid = isValid;
            s.dirty = !clean;        // equal to the saved split => not dirty
            s.savedOk = clean;
            s.error = '';
            s.payload = payload;
            this.refreshAllocationCell(row);
        },

        // The cheap "overrides only" save — merges allocation into the row's existing
        // overrides (so other override keys aren't clobbered) and PATCHes without a recalc.
        // `advance` (a per-row Save, not Save All) collapses this row on success and opens
        // the next timecard that isn't fully allocated.
        async saveRow(row, advance) {
            const s = this.uiState[row.id] || (this.uiState[row.id] = freshState());
            if (!s.payload || !s.valid || s.saving) return false;
            s.saving = true;
            s.error = '';

            const overrides = decode(row.overrides) || {};
            if (Array.isArray(s.payload) && s.payload.length) overrides.allocation = s.payload;
            else delete overrides.allocation;

            let ok = false;
            try {
                const res = await this.oasisApi.patch('/timecard/', {
                    isBatchUpdate: true,
                    id: row.id,
                    overrides: overrides,
                });
                ok = res && res.status >= 200 && res.status < 300;
                if (ok) {
                    row.overrides = overrides;
                    s.dirty = false;
                    s.savedOk = true;
                } else {
                    s.error = 'Save failed (' + (res ? res.status : '?') + ')';
                }
            } catch (e) {
                s.error = (e && e.message) ? e.message : 'Save failed';
                ok = false;
            } finally {
                s.saving = false;
                this.refreshAllocationCell(row);
            }
            if (ok && advance) this.advanceToNext(row);
            return ok;
        },

        // Collapse the just-saved row and open the next timecard (in current sort/filter
        // order) that is not yet fully (100%) allocated to projects.
        advanceToNext(savedRow) {
            if (!this.gridApi) return;
            const api = this.gridApi;
            const nodes = [];
            api.forEachNodeAfterFilterAndSort((n) => { if (n && n.data && !n.detail) nodes.push(n); });
            const savedNode = nodes.find((n) => n.data && n.data.id === savedRow.id);
            if (savedNode) savedNode.setExpanded(false);
            const startIdx = savedNode ? nodes.indexOf(savedNode) : -1;

            let target = null;
            for (let i = startIdx + 1; i < nodes.length; i++) {
                if (this.projectAllocatedPct(nodes[i].data) < 100) { target = nodes[i]; break; }
            }
            // wrap around to earlier rows if nothing after it still needs allocating
            if (!target) {
                for (let i = 0; i < startIdx; i++) {
                    if (this.projectAllocatedPct(nodes[i].data) < 100) { target = nodes[i]; break; }
                }
            }
            if (target) {
                target.setExpanded(true);
                api.ensureNodeVisible(target);
            }
        },

        async saveAll() {
            if (this.savingAll) return;
            this.savingAll = true;
            this.savedCount = 0;
            let saved = 0;
            try {
                const dirty = this.rowData.filter((r) => {
                    const s = this.uiState[r.id];
                    return s && s.touched && s.dirty && s.valid;
                });
                for (const row of dirty) {
                    const ok = await this.saveRow(row);
                    if (ok) saved++;
                }
                this.savedCount = saved;
            } finally {
                this.savingAll = false;
            }
        },

        refreshAllocationCell(row) {
            if (this.gridApi) this.gridApi.refreshCells({ columns: ['_allocation', '_alloc_pct'], force: true });
        },

        // ---- grand summary helpers (mirror allocation.vue's pay model) ----
        _num(v) { const n = parseFloat(v); return isNaN(n) ? 0 : n; },
        _round2(v) { return Math.round((this._num(v) + Number.EPSILON) * 100) / 100; },
        _weekPay(b)   { return this._num((((b || {}).week || {}).total || {}).pay); },
        _weekHours(b) { return this._num((((b || {}).week || {}).total || {}).hours); },
        _weekAllow(b) { return this._num((((b || {}).week || {}).allowances || {}).pay); },
        _isBiweekly(b2) { return !!(b2 && b2.week && b2.week.total); },

        // Format money the same way the grid's Gross Pay column does.
        money(v) { return (Number(v) || 0).formatNumber(); },
    },

    computed: {
        unsavedCount() {
            return this.rowData.filter((r) => {
                const s = this.uiState[r.id];
                return s && s.touched && s.dirty && s.valid;
            }).length;
        },

        // Grand allocation summary across every timecard: for each PROJECT, the total
        // wages / allowances / hours allocated to it, plus an UNALLOCATED remainder and a
        // full TOTAL — the same column model as the per-timecard <allocation> table, but
        // read-only and aggregated. Uses the live edited split where a row has one, else
        // the saved overrides.allocation (same source as the Total Allocated column).
        allocationSummary() {
            const byKey = {};
            let grossPay = 0, grossAllow = 0, grossHours = 0;

            for (const row of this.rowData) {
                const b  = decode(row.broken_object)  || {};
                const b2 = decode(row.broken_object2) || {};
                const bi = this._isBiweekly(b2);
                grossPay   += this._weekPay(b)   + (bi ? this._weekPay(b2)   : 0);
                grossAllow += this._weekAllow(b) + (bi ? this._weekAllow(b2) : 0);
                grossHours += this._weekHours(b) + (bi ? this._weekHours(b2) : 0);

                const s = this.uiState[row.id];
                const alloc = (s && s.payload) ? s.payload : ((decode(row.overrides) || {}).allocation);
                if (!Array.isArray(alloc)) continue;
                for (const r of alloc) {
                    if (!r) continue;
                    const key = (r.id == null) ? NO_PROJECT : String(r.id);
                    if (!byKey[key]) {
                        byKey[key] = {
                            id: key,
                            name: r.name || (key === NO_PROJECT ? '(No project)' : ('Project ' + key)),
                            amount: 0, allowances: 0, hours: 0,
                        };
                    }
                    byKey[key].amount     += this._num(r.amount);
                    byKey[key].allowances += this._num(r.allowances);
                    byKey[key].hours      += this._num(r.hours);
                }
            }

            const grossWages = this._round2(grossPay - grossAllow);
            const pct = (combined) => (grossPay ? Math.round(combined / grossPay * 10000) / 100 : 0);

            const rows = Object.keys(byKey).map((k) => {
                const e = byKey[k];
                return {
                    id: k, name: e.name,
                    percent: pct(e.amount + e.allowances),
                    hours: this._round2(e.hours),
                    amount: this._round2(e.amount),
                    allowances: this._round2(e.allowances),
                };
            });
            // Real projects first (by name); the "(No project)" line sinks to the bottom.
            rows.sort((a, b) =>
                (a.id === NO_PROJECT) - (b.id === NO_PROJECT) ||
                String(a.name).localeCompare(String(b.name)));

            const sum = (f) => rows.reduce((t, r) => t + r[f], 0);
            const allocWages = this._round2(sum('amount'));
            const allocAllow = this._round2(sum('allowances'));
            const allocHours = this._round2(sum('hours'));

            return {
                rows,
                unallocated: {
                    percent: this._round2(100 - pct(allocWages + allocAllow)),
                    hours: this._round2(grossHours - allocHours),
                    amount: this._round2(grossWages - allocWages),
                    allowances: this._round2(grossAllow - allocAllow),
                },
                total: {
                    percent: 100,
                    hours: this._round2(grossHours),
                    amount: grossWages,
                    allowances: this._round2(grossAllow),
                },
            };
        },
    },

    mounted() {
        this.loadAll();
    },
});

app.mount('#allocateApp');
